import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { AppContext } from '@/types';
import * as events from '@/events';
import {
  buildDeclineUiAnalyticsPayload,
  getActionForBreakdownType,
  getBreakdownDescriptionKey,
  getDeclineHeadlineKey,
  getDeclineSubtitleKey,
  getVisibleDeclineBreakdown,
  isPayInFullOnlyAvailable,
  isZeroRecommendedLimit,
} from '@/modules/decline-ui/lib/decline-ui-helpers';
import {
  emitDeclineUiActionClickedTracking,
  emitDeclineUiPageShownTracking,
} from '@/shared/tracking/analytics-decline-ui-events';
import { BreakdownItemCard } from '@/modules/decline-ui/components/breakdown-item-card';
import { LimitComparisonCard } from '@/modules/decline-ui/components/limit-comparison-card';
import { DeclineWaveDivider } from '@/modules/decline-ui/components/decline-ui-graphics';
import { useDeclineUiActionsGate } from '@/shared/abt';
import { canOpenDeeplink, openDeeplink } from '@/shared/lib/deeplink';
import {
  CREDIT_DECLINE_BREAKDOWN_TYPE,
  type CreditDeclineAction,
} from '@/shared/types/credit-decline';

interface DeclineUiScreenProps {
  ctx: AppContext;
}

export function DeclineUiScreen({
  ctx,
}: DeclineUiScreenProps): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const hasTrackedPageShown = useRef(false);
  const creditDeclineInfo = ctx.session.creditDeclineInfo;
  const actionsGate = useDeclineUiActionsGate();

  const showPayInFull = useMemo(
    () => isPayInFullOnlyAvailable(ctx.session.paymentOptions ?? []),
    [ctx.session.paymentOptions],
  );

  // Actions only render when the gate is on, we're in an environment where the
  // deeplink can open, and the BE returned an action for that row.
  const deeplinkSupported = useMemo(() => canOpenDeeplink(), []);
  const actionsEnabled = actionsGate.value && deeplinkSupported;

  const visibleBreakdown = useMemo(
    () => getVisibleDeclineBreakdown(creditDeclineInfo?.breakdown ?? []),
    [creditDeclineInfo?.breakdown],
  );

  const shownActionTypes = useMemo(() => {
    if (!actionsEnabled || !creditDeclineInfo) {
      return [] as string[];
    }
    return visibleBreakdown
      .map(
        (item) =>
          getActionForBreakdownType(creditDeclineInfo.actions, item.type)?.type,
      )
      .filter((type): type is NonNullable<typeof type> => Boolean(type));
  }, [actionsEnabled, creditDeclineInfo, visibleBreakdown]);

  const handleActionClick = useCallback(
    (action: CreditDeclineAction) => {
      emitDeclineUiActionClickedTracking(ctx.session, action.type);
      ctx.sendEvent(events.DECLINE_UI_ACTION_CLICKED, {
        action_type: action.type,
        checkout_id: ctx.session.id,
        order_id: ctx.session.order?.id ?? '',
      });
      openDeeplink(action.action);
    },
    [ctx],
  );

  useEffect(() => {
    // Wait for Statsig so actions_shown matches what the user actually sees.
    // Firing while the gate is still loading records [] and never retries
    // (hasTrackedPageShown locks after the first emit).
    if (
      !creditDeclineInfo ||
      !actionsGate.isReady ||
      hasTrackedPageShown.current
    ) {
      return;
    }

    hasTrackedPageShown.current = true;
    const analyticsPayload = buildDeclineUiAnalyticsPayload(
      ctx.session,
      creditDeclineInfo,
      showPayInFull,
      shownActionTypes,
    );
    emitDeclineUiPageShownTracking(ctx.session, analyticsPayload);
    ctx.sendEvent(events.DECLINE_UI_PAGE_SHOWN, analyticsPayload);
  }, [
    actionsGate.isReady,
    creditDeclineInfo,
    ctx,
    showPayInFull,
    shownActionTypes,
  ]);

  if (!creditDeclineInfo) {
    return null;
  }

  const headlineKey = getDeclineHeadlineKey(creditDeclineInfo.declineCode);
  const hasZeroRecommendedLimit = isZeroRecommendedLimit(
    creditDeclineInfo.recommendedLimit,
  );
  const headline =
    creditDeclineInfo.headline?.trim() ||
    t(`decline_ui.headline.${headlineKey}`);
  const subtitleKey = getDeclineSubtitleKey(hasZeroRecommendedLimit);
  const subtitle =
    creditDeclineInfo.subtitle?.trim() ||
    t(`decline_ui.subtitle.${subtitleKey}`);

  return (
    <div className="flex flex-col bg-[#F4F6F9] rtl:font-arabic">
      <div className="flex flex-col items-center gap-6 bg-white px-5 pb-2 pt-6">
        <div className="flex w-full flex-col gap-2 text-center">
          <h1 className="text-[28px] font-bold leading-10 tracking-[-0.42px] text-[#16181D]">
            {headline}
          </h1>
          <p className="whitespace-pre-line text-[16px] font-semibold leading-6 tracking-[-0.24px] text-[#585B7A]">
            {subtitle}
          </p>
        </div>

        {hasZeroRecommendedLimit ? null : (
          <LimitComparisonCard
            orderAmount={creditDeclineInfo.orderAmount}
            recommendedLimit={creditDeclineInfo.recommendedLimit}
            currency={ctx.session.order.currency}
            language={i18n.language}
          />
        )}
      </div>

      {visibleBreakdown.length > 0 ? (
        <>
          <DeclineWaveDivider />
          <div className="relative z-[1] flex w-full flex-col gap-4 rounded-t-[32px] bg-white px-5 pb-6 pt-4">
          <h2 className="text-[18px] font-semibold leading-6 tracking-[-0.27px] text-[#16181D]">
            {t('decline_ui.breakdown.section_title')}
          </h2>

          {visibleBreakdown.map((item) => {
            const action = actionsEnabled
              ? getActionForBreakdownType(creditDeclineInfo.actions, item.type)
              : undefined;
            // External obligations are informational for now (no deeplink /
            // chevron). Still show the guidance copy whenever the row is present.
            const showInformationalDescription =
              item.type ===
              CREDIT_DECLINE_BREAKDOWN_TYPE.EXTERNAL_OBLIGATIONS;
            return (
              <BreakdownItemCard
                key={item.type}
                item={item}
                currency={ctx.session.order.currency}
                language={i18n.language}
                descriptionKey={
                  action || showInformationalDescription
                    ? getBreakdownDescriptionKey(item.type)
                    : undefined
                }
                onActionClick={
                  action ? () => handleActionClick(action) : undefined
                }
              />
            );
          })}
          </div>
        </>
      ) : null}
    </div>
  );
}
